<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>
    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="EPF Settings"
      text="Edit EPF Settings"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field
              v-model="localEpfSetting.total_gross_without_additional_remuneration"
              v-validate="'twoDecimalValue'" 
              type="number"
              :error-messages="errors.collect('total_gross_without_additional_remuneration')"
              data-vv-name="total_gross_without_additional_remuneration"                
              data-vv-as="gross"      
              prefix="RM"          
              label="Gross"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="localEpfSetting.total_gross_with_additional_remuneration"
              v-validate="'twoDecimalValue'" 
              type="number"
              :error-messages="errors.collect('total_gross_with_additional_remuneration')"
              data-vv-name="total_gross_with_additional_remuneration"                
              data-vv-as="gross + additional remuneration"  
              prefix="RM"          
              label="Gross + Additional Remuneration"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="localEpfSetting.employee_rate"
              v-validate="'twoDecimalValue'" 
              type="number"
              :error-messages="errors.collect('employee_rate')"
              data-vv-name="employee_rate"                
              data-vv-as="employee rate"                
              label="Employer Rate"
              prefix="RM"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="localEpfSetting.employer_rate"
              v-validate="'twoDecimalValue'" 
              type="number"
              :error-messages="errors.collect('employer_rate')"
              data-vv-name="employer_rate"                
              data-vv-as="employer rate"                
              label="Employer Rate"
              prefix="RM"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="localEpfSetting.description"
              v-validate="'max:1000'"
              :rows="1"
              :error-messages="errors.collect('description')"
              data-vv-name="description"     
              data-vv-as="description"           
              label="Description"
            />
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="localEpfSetting.category"
              v-validate="'required'"
              :error-messages="errors.collect('category')"
              :items="categories"
              data-vv-name="key"
              data-vv-as="category"
              item-text="name"
              item-value="key"
              label="EPF Category / Part"
            />
          </v-col>
        </v-row> 
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import Big from 'big.js'
  export default {
    inject: ['$validator'],
    props: {
      epfsetting: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        categories: [
          { key: 'A', name: 'A' },
          { key: 'E', name: 'E' },
        ],
        dialog: null,
        progressing: false,
        localEpfSetting: null,
      }
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.localEpfSetting = { ...this.epfsetting }
        this.localEpfSetting.total_gross_without_additional_remuneration = this.localEpfSetting.total_gross_without_additional_remuneration ? (this.localEpfSetting.total_gross_without_additional_remuneration / 100).toFixed(2) : null
        this.localEpfSetting.total_gross_with_additional_remuneration = this.localEpfSetting.total_gross_with_additional_remuneration ? (this.localEpfSetting.total_gross_with_additional_remuneration / 100).toFixed(2) : null
        this.localEpfSetting.employee_rate = this.localEpfSetting.employee_rate
        this.localEpfSetting.employer_rate = this.localEpfSetting.employer_rate
        this.localEpfSetting.description = this.localEpfSetting.description
        this.localEpfSetting.category = this.localEpfSetting.category
      },
    },
    mounted () {
      let _this = this

      this.$validator.extend('twoDecimalValue', {
        getMessage (field, args) {
          return 'Only 2 Decimals are allowed'
        },
        validate (value, args) {
          return _this.isValidNumbers(value)
        },
      })
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            this.setProgressing(true)
            this.$api.epfSettings().update(this.localEpfSetting.id, this.prepareFormData())
              .then(response => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ message: response.data.status })
                }
              })
              .catch(() => {})
              .then(() => {
                this.setProgressing(false)
                this.dialog = false 
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        return {
          total_gross_without_additional_remuneration: this.localEpfSetting.total_gross_without_additional_remuneration ? this.localEpfSetting.total_gross_without_additional_remuneration * 100 : null,
          total_gross_with_additional_remuneration: this.localEpfSetting.total_gross_with_additional_remuneration ? this.localEpfSetting.total_gross_with_additional_remuneration * 100 : null,
          employee_rate: this.localEpfSetting.employee_rate ? this.localEpfSetting.employee_rate : null,
          employer_rate: this.localEpfSetting.employer_rate ? this.localEpfSetting.employer_rate : null,
          description: this.localEpfSetting.description,
          category: this.localEpfSetting.category,
        }
      },

      isValidNumbers (value) {
        return (new Big(value).times(10000) % 100 === 0)
      },
    },
  }
</script>
